/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import SEO from '../components/SEO';
import {
  Container,
  Box,
  Typography,
  Paper,
  Tabs,
  Tab,
  Avatar,
  Button,
  Chip,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Alert,
  Skeleton,
  useMediaQuery,
  useTheme,
  Card,
  CardContent,
  Stack,
} from '@mui/material';
import {
  collection,
  query,
  where,
  onSnapshot,
  Timestamp,
} from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../context/AuthContext';
import PageBanner from '../components/PageBanner';
import LogoutIcon from '@mui/icons-material/Logout';
import TempleHinduIcon from '@mui/icons-material/TempleHindu';
import PaymentIcon from '@mui/icons-material/Payment';
import DirectionsBoatIcon from '@mui/icons-material/DirectionsBoat';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import RefreshIcon from '@mui/icons-material/Refresh';
import CurrencyRupeeIcon from '@mui/icons-material/CurrencyRupee';
import PersonIcon from '@mui/icons-material/Person';
import StarIcon from '@mui/icons-material/Star';
import EditCalendarIcon from '@mui/icons-material/EditCalendar';
import VolunteerActivismIcon from '@mui/icons-material/VolunteerActivism';
import EmailIcon from '@mui/icons-material/Email';
import ReplyIcon from '@mui/icons-material/Reply';
import FeedbackIcon from '@mui/icons-material/Feedback';

const GOLD = '#d4af37';

/* ------- Firestore interfaces ------- */
interface VazhipadBooking {
  id: string;
  poojaName: string;
  devotee: string;
  star: string;
  date: string;
  quantity: number;
  amount: number;
  status: 'confirmed' | 'pending' | 'cancelled';
  paymentId?: string;
  createdAt: Timestamp | null;
}

interface HouseboatBooking {
  id: string;
  packageName: string;
  guests: number;
  checkIn: string;
  checkOut: string;
  amount: number;
  status: 'confirmed' | 'pending' | 'cancelled';
  paymentId?: string;
  createdAt: Timestamp | null;
}

interface PaymentRecord {
  id: string;
  type: 'vazhipad' | 'houseboat' | 'donation' | 'other';
  description: string;
  amount: number;
  status: 'success' | 'failed' | 'pending' | 'refunded';
  razorpayId?: string;
  createdAt: Timestamp | null;
}

interface DonationRecord {
  id: string;
  cause: string;
  amount: number;
  donorName: string;
  message?: string;
  status: 'success' | 'pending' | 'failed';
  paymentId?: string;
  createdAt: Timestamp | null;
}

interface ContactMessage {
  id: string;
  name: string;
  email: string;
  phone?: string;
  subject: string;
  message: string;
  formType: 'contact' | 'feedback';
  category?: string;
  rating?: number;
  read: boolean;
  adminReply?: string;
  repliedAt: Timestamp | null;
  createdAt: Timestamp | null;
}

/* ------- Tab panel ------- */
function TabPanel({ children, value, index }: { children: React.ReactNode; value: number; index: number }) {
  return (
    <Box role="tabpanel" hidden={value !== index} sx={{ pt: 2.5 }}>
      {value === index && children}
    </Box>
  );
}

/* ------- Status chip ------- */
function StatusChip({ status }: { status: string }) {
  const map: Record<string, { color: 'success' | 'warning' | 'error' | 'default' | 'info'; icon: React.ReactElement }> = {
    confirmed: { color: 'success', icon: <CheckCircleOutlineIcon sx={{ fontSize: 16 }} /> },
    success: { color: 'success', icon: <CheckCircleOutlineIcon sx={{ fontSize: 16 }} /> },
    pending: { color: 'warning', icon: <HourglassEmptyIcon sx={{ fontSize: 16 }} /> },
    failed: { color: 'error', icon: <ErrorOutlineIcon sx={{ fontSize: 16 }} /> },
    cancelled: { color: 'default', icon: <ErrorOutlineIcon sx={{ fontSize: 16 }} /> },
    refunded: { color: 'info', icon: <RefreshIcon sx={{ fontSize: 16 }} /> },
  };
  const cfg = map[status] || map['pending'];
  return (
    <Chip
      label={status.charAt(0).toUpperCase() + status.slice(1)}
      color={cfg.color}
      size="small"
      icon={cfg.icon}
      variant="outlined"
      sx={{ fontWeight: 600, fontSize: '0.72rem', borderRadius: '8px' }}
    />
  );
}

/* ------- Empty state ------- */
function EmptyState({ icon, title, subtitle }: { icon: React.ReactNode; title: string; subtitle: string }) {
  return (
    <Box sx={{ textAlign: 'center', py: { xs: 5, md: 8 }, px: 2 }}>
      <Box sx={{ color: '#ccc', mb: 2 }}>{icon}</Box>
      <Typography sx={{ fontWeight: 700, fontSize: { xs: '0.92rem', md: '1.1rem' }, color: '#555', mb: 0.5 }}>{title}</Typography>
      <Typography sx={{ color: '#999', fontSize: '0.85rem' }}>{subtitle}</Typography>
    </Box>
  );
}

/* ------- Stat card ------- */
function StatCard({ icon, label, value, color }: { icon: React.ReactNode; label: string; value: string | number; color: string }) {
  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        borderRadius: 3,
        border: '1px solid #f0ece4',
        background: 'linear-gradient(135deg, #fff 0%, #fdfcf9 100%)',
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        flex: 1,
        minWidth: 140,
      }}
    >
      <Box
        sx={{
          width: 42,
          height: 42,
          borderRadius: '12px',
          bgcolor: color,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        {icon}
      </Box>
      <Box>
        <Typography sx={{ fontSize: '0.7rem', color: '#999', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
          {label}
        </Typography>
        <Typography sx={{ fontSize: { xs: '0.95rem', md: '1.15rem' }, fontWeight: 700, color: '#333', lineHeight: 1.2 }}>
          {value}
        </Typography>
      </Box>
    </Paper>
  );
}

/* ================================================================
   MAIN COMPONENT
   ================================================================ */
export default function MyDevotions() {
  const { user, logout, openLoginModal } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [tab, setTab] = useState(0);
  const [vazhipads, setVazhipads] = useState<VazhipadBooking[]>([]);
  const [houseboats, setHouseboats] = useState<HouseboatBooking[]>([]);
  const [payments, setPayments] = useState<PaymentRecord[]>([]);
  const [donations, setDonations] = useState<DonationRecord[]>([]);
  const [messages, setMessages] = useState<ContactMessage[]>([]);
  const [loadingV, setLoadingV] = useState(true);
  const [loadingH, setLoadingH] = useState(true);
  const [loadingP, setLoadingP] = useState(true);
  const [loadingD, setLoadingD] = useState(true);
  const [loadingM, setLoadingM] = useState(true);

  /* ---- redirect if not logged in ---- */
  useEffect(() => {
    if (!user) {
      openLoginModal();
      navigate('/');
    }
  }, [user, navigate, openLoginModal]);

  /* ---- Firestore listeners ---- */
  useEffect(() => {
    if (!user) return;
    const uid = user.uid;

    // Vazhipad bookings — use simple query without orderBy to avoid composite index requirement
    const qV = query(collection(db, 'vazhipad_bookings'), where('userId', '==', uid));
    const unsubV = onSnapshot(qV, (snap) => {
      const docs = snap.docs.map((d) => ({ id: d.id, ...d.data() } as VazhipadBooking));
      docs.sort((a, b) => {
        const ta = a.createdAt ? a.createdAt.toMillis() : 0;
        const tb = b.createdAt ? b.createdAt.toMillis() : 0;
        return tb - ta;
      });
      setVazhipads(docs);
      setLoadingV(false);
    }, (err) => {
      console.error('Vazhipad query error:', err);
      setLoadingV(false);
    });

    // Houseboat bookings
    const qH = query(collection(db, 'houseboat_bookings'), where('userId', '==', uid));
    const unsubH = onSnapshot(qH, (snap) => {
      const docs = snap.docs.map((d) => ({ id: d.id, ...d.data() } as HouseboatBooking));
      docs.sort((a, b) => {
        const ta = a.createdAt ? a.createdAt.toMillis() : 0;
        const tb = b.createdAt ? b.createdAt.toMillis() : 0;
        return tb - ta;
      });
      setHouseboats(docs);
      setLoadingH(false);
    }, (err) => {
      console.error('Houseboat query error:', err);
      setLoadingH(false);
    });

    // Payments
    const qP = query(collection(db, 'payments'), where('userId', '==', uid));
    const unsubP = onSnapshot(qP, (snap) => {
      const docs = snap.docs.map((d) => ({ id: d.id, ...d.data() } as PaymentRecord));
      docs.sort((a, b) => {
        const ta = a.createdAt ? a.createdAt.toMillis() : 0;
        const tb = b.createdAt ? b.createdAt.toMillis() : 0;
        return tb - ta;
      });
      setPayments(docs);
      setLoadingP(false);
    }, (err) => {
      console.error('Payments query error:', err);
      setLoadingP(false);
    });

    // Donations
    const qD = query(collection(db, 'donations'), where('userId', '==', uid));
    const unsubD = onSnapshot(qD, (snap) => {
      const docs = snap.docs.map((d) => ({ id: d.id, ...d.data() } as DonationRecord));
      docs.sort((a, b) => {
        const ta = a.createdAt ? a.createdAt.toMillis() : 0;
        const tb = b.createdAt ? b.createdAt.toMillis() : 0;
        return tb - ta;
      });
      setDonations(docs);
      setLoadingD(false);
    }, (err) => {
      console.error('Donations query error:', err);
      setLoadingD(false);
    });

    // Contact messages — match by email so both old and new messages appear
    const qM = query(collection(db, 'contact_messages'), where('email', '==', user.email));
    const unsubM = onSnapshot(qM, (snap) => {
      const docs = snap.docs.map((d) => ({ id: d.id, ...d.data() } as ContactMessage));
      docs.sort((a, b) => {
        const ta = a.createdAt ? a.createdAt.toMillis() : 0;
        const tb = b.createdAt ? b.createdAt.toMillis() : 0;
        return tb - ta;
      });
      setMessages(docs);
      setLoadingM(false);
    }, (err) => {
      console.error('Messages query error:', err);
      setLoadingM(false);
    });

    return () => { unsubV(); unsubH(); unsubP(); unsubD(); unsubM(); };
  }, [user]);

  if (!user) return null;

  /* ---- computed stats ---- */
  const totalBookings = vazhipads.length + houseboats.length;
  const totalSpent = payments.filter((p) => p.status === 'success').reduce((a, p) => a + p.amount, 0);
  const failedPayments = payments.filter((p) => p.status === 'failed').length;
  const totalDonated = donations.filter((d) => d.status === 'success').reduce((a, d) => a + d.amount, 0);

  /* ---- format date ---- */
  const fmtDate = (ts: Timestamp | null | string) => {
    if (!ts) return '—';
    const d = typeof ts === 'string' ? new Date(ts) : ts.toDate();
    return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  /* ---- loading skeleton ---- */
  const SkeletonRows = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      {[1, 2, 3].map((i) => (
        <Skeleton key={i} variant="rounded" height={60} sx={{ borderRadius: 2 }} />
      ))}
    </Box>
  );

  return (
    <>
      <SEO title="My Devotions" description="View your devotion history, pooja bookings, and spiritual journey at Muchukunnu Sri Kotta-Kovilakam Kshethram." path="/my-devotions" keywords="my devotions, booking history, pooja history" />
      <PageBanner title="My Devotions" subtitle="Your spiritual journey at a glance" />

      <Container maxWidth="lg" sx={{ mt: -8, position: 'relative', zIndex: 2, pb: 8 }}>
        {/* ================ PROFILE CARD ================ */}
        <Paper
          elevation={0}
          sx={{
            p: { xs: 2.5, md: 3.5 },
            borderRadius: 4,
            border: `3px solid ${GOLD}`,
            boxShadow: '0 20px 60px rgba(212,175,55,0.18)',
            background: 'linear-gradient(135deg, #fff 0%, #fdfcf9 100%)',
            mb: 3,
            position: 'relative',
            overflow: 'hidden',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: 4,
              background: `linear-gradient(90deg, ${GOLD}, #e5c158, ${GOLD})`,
            },
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            {/* Avatar */}
            {user.photoURL ? (
              <Avatar src={user.photoURL} alt={user.displayName || 'User'} sx={{ width: 56, height: 56, border: `2.5px solid ${GOLD}` }} />
            ) : (
              <Avatar
                sx={{
                  width: 56,
                  height: 56,
                  bgcolor: GOLD,
                  color: '#1a1a2e',
                  fontSize: { xs: '1.1rem', md: '1.4rem' },
                  fontWeight: 700,
                  border: '2.5px solid #e5c158',
                }}
              >
                {(user.displayName || user.email || 'U').charAt(0).toUpperCase()}
              </Avatar>
            )}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography sx={{ fontWeight: 700, fontSize: { xs: '1.1rem', md: '1.25rem' }, color: '#222', lineHeight: 1.2 }}>
                {user.displayName || 'Devotee'}
              </Typography>
              <Typography sx={{ color: '#888', fontSize: '0.82rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {user.email}
              </Typography>
            </Box>
            <Button
              variant="outlined"
              size="small"
              startIcon={<LogoutIcon sx={{ fontSize: 16 }} />}
              onClick={logout}
              sx={{
                borderColor: '#e0dcd4',
                color: '#888',
                fontWeight: 600,
                fontSize: '0.78rem',
                borderRadius: '10px',
                textTransform: 'none',
                '&:hover': { borderColor: '#d32f2f', color: '#d32f2f', bgcolor: 'rgba(211,47,47,0.04)' },
              }}
            >
              Sign Out
            </Button>
          </Box>

          {/* ---- stat strip ---- */}
          <Box sx={{ display: 'flex', gap: 1.5, mt: 2.5, flexWrap: 'wrap' }}>
            <StatCard
              icon={<TempleHinduIcon sx={{ color: '#fff', fontSize: 20 }} />}
              label="Total Bookings"
              value={totalBookings}
              color="rgba(212,175,55,0.8)"
            />
            <StatCard
              icon={<CurrencyRupeeIcon sx={{ color: '#fff', fontSize: 20 }} />}
              label="Total Spent"
              value={`₹${totalSpent.toLocaleString('en-IN')}`}
              color="rgba(46,125,50,0.75)"
            />
            <StatCard
              icon={<VolunteerActivismIcon sx={{ color: '#fff', fontSize: 20 }} />}
              label="Total Donated"
              value={`₹${totalDonated.toLocaleString('en-IN')}`}
              color="rgba(156,39,176,0.75)"
            />
            <StatCard
              icon={<ErrorOutlineIcon sx={{ color: '#fff', fontSize: 20 }} />}
              label="Failed Payments"
              value={failedPayments}
              color={failedPayments > 0 ? 'rgba(211,47,47,0.75)' : 'rgba(158,158,158,0.5)'}
            />
          </Box>
        </Paper>

        {/* ================ TABS CARD ================ */}
        <Paper
          elevation={0}
          sx={{
            borderRadius: 4,
            border: `2px solid #f0ece4`,
            background: '#fff',
            overflow: 'hidden',
          }}
        >
          <Tabs
            value={tab}
            onChange={(_, v) => setTab(v)}
            variant={isMobile ? 'fullWidth' : 'standard'}
            sx={{
              bgcolor: '#faf9f6',
              borderBottom: '1px solid #f0ece4',
              px: { xs: 0, md: 1 },
              '& .MuiTab-root': {
                textTransform: 'none',
                fontWeight: 600,
                fontSize: { xs: '0.78rem', md: '0.88rem' },
                color: '#999',
                minHeight: 52,
                '&.Mui-selected': { color: GOLD },
              },
              '& .MuiTabs-indicator': { bgcolor: GOLD, height: 3, borderRadius: '3px 3px 0 0' },
            }}
          >
            <Tab icon={<TempleHinduIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Vazhipadu" />
            <Tab icon={<DirectionsBoatIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Houseboat" />
            <Tab icon={<VolunteerActivismIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Donations" />
            <Tab icon={<PaymentIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Payments" />
            <Tab icon={<EmailIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Messages" />
          </Tabs>

          <Box sx={{ p: { xs: 2, md: 3 } }}>
            {/* ================ TAB 0: Vazhipad Bookings ================ */}
            <TabPanel value={tab} index={0}>
              {loadingV ? (
                <SkeletonRows />
              ) : vazhipads.length === 0 ? (
                <EmptyState
                  icon={<TempleHinduIcon sx={{ fontSize: 56 }} />}
                  title="No Vazhipadu Bookings Yet"
                  subtitle="Your pooja and vazhipadu bookings will appear here once you make a booking."
                />
              ) : isMobile ? (
                /* ---- mobile cards ---- */
                <Stack spacing={1.5}>
                  {vazhipads.map((b) => (
                    <Card
                      key={b.id}
                      variant="outlined"
                      sx={{ borderRadius: 3, borderColor: '#f0ece4', '&:hover': { borderColor: GOLD, boxShadow: `0 2px 12px rgba(212,175,55,0.12)` } }}
                    >
                      <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.8 }}>
                          <Typography sx={{ fontWeight: 700, fontSize: '0.9rem', color: '#333' }}>{b.poojaName}</Typography>
                          <StatusChip status={b.status} />
                        </Box>
                        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', color: '#777', fontSize: '0.78rem' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
                            <PersonIcon sx={{ fontSize: 14, color: '#bbb' }} />
                            <Typography variant="caption" sx={{ color: '#666' }}>{b.devotee}</Typography>
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
                            <StarIcon sx={{ fontSize: 14, color: '#bbb' }} />
                            <Typography variant="caption" sx={{ color: '#666' }}>{b.star}</Typography>
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
                            <CalendarMonthIcon sx={{ fontSize: 14, color: '#bbb' }} />
                            <Typography variant="caption" sx={{ color: '#666' }}>{b.date}</Typography>
                          </Box>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
                          <Typography sx={{ fontWeight: 700, color: GOLD, fontSize: '0.95rem' }}>
                            ₹{(b.amount || 0).toLocaleString('en-IN')}
                          </Typography>
                          <Typography sx={{ color: '#bbb', fontSize: '0.7rem' }}>
                            {fmtDate(b.createdAt)}
                          </Typography>
                        </Box>
                      </CardContent>
                    </Card>
                  ))}
                </Stack>
              ) : (
                /* ---- desktop table ---- */
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ '& th': { fontWeight: 700, color: '#777', fontSize: '0.78rem', borderColor: '#f0ece4', textTransform: 'uppercase', letterSpacing: '0.04em' } }}>
                        <TableCell>Pooja</TableCell>
                        <TableCell>Devotee</TableCell>
                        <TableCell>Star</TableCell>
                        <TableCell>Date</TableCell>
                        <TableCell align="right">Amount</TableCell>
                        <TableCell align="center">Status</TableCell>
                        <TableCell>Booked On</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {vazhipads.map((b) => (
                        <TableRow key={b.id} sx={{ '& td': { borderColor: '#f5f3ee', fontSize: '0.85rem', color: '#444' }, '&:hover': { bgcolor: '#fdfcf9' } }}>
                          <TableCell sx={{ fontWeight: 600, color: '#333 !important' }}>{b.poojaName}</TableCell>
                          <TableCell>{b.devotee}</TableCell>
                          <TableCell>{b.star}</TableCell>
                          <TableCell>{b.date}</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600, color: `${GOLD} !important` }}>₹{(b.amount || 0).toLocaleString('en-IN')}</TableCell>
                          <TableCell align="center"><StatusChip status={b.status} /></TableCell>
                          <TableCell sx={{ color: '#aaa !important', fontSize: '0.78rem !important' }}>{fmtDate(b.createdAt)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </TabPanel>

            {/* ================ TAB 1: Houseboat Bookings ================ */}
            <TabPanel value={tab} index={1}>
              {loadingH ? (
                <SkeletonRows />
              ) : houseboats.length === 0 ? (
                <EmptyState
                  icon={<DirectionsBoatIcon sx={{ fontSize: 56 }} />}
                  title="No Houseboat Bookings"
                  subtitle="Houseboat and tour bookings will show up here."
                />
              ) : isMobile ? (
                <Stack spacing={1.5}>
                  {houseboats.map((b) => (
                    <Card
                      key={b.id}
                      variant="outlined"
                      sx={{ borderRadius: 3, borderColor: '#f0ece4', '&:hover': { borderColor: GOLD, boxShadow: `0 2px 12px rgba(212,175,55,0.12)` } }}
                    >
                      <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.8 }}>
                          <Typography sx={{ fontWeight: 700, fontSize: '0.9rem', color: '#333' }}>{b.packageName}</Typography>
                          <StatusChip status={b.status} />
                        </Box>
                        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
                            <PersonIcon sx={{ fontSize: 14, color: '#bbb' }} />
                            <Typography variant="caption" sx={{ color: '#666' }}>{b.guests} guests</Typography>
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
                            <CalendarMonthIcon sx={{ fontSize: 14, color: '#bbb' }} />
                            <Typography variant="caption" sx={{ color: '#666' }}>{b.checkIn} → {b.checkOut}</Typography>
                          </Box>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
                          <Typography sx={{ fontWeight: 700, color: GOLD, fontSize: '0.95rem' }}>
                            ₹{(b.amount || 0).toLocaleString('en-IN')}
                          </Typography>
                          <Typography sx={{ color: '#bbb', fontSize: '0.7rem' }}>
                            {fmtDate(b.createdAt)}
                          </Typography>
                        </Box>
                      </CardContent>
                    </Card>
                  ))}
                </Stack>
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ '& th': { fontWeight: 700, color: '#777', fontSize: '0.78rem', borderColor: '#f0ece4', textTransform: 'uppercase', letterSpacing: '0.04em' } }}>
                        <TableCell>Package</TableCell>
                        <TableCell>Guests</TableCell>
                        <TableCell>Check-In</TableCell>
                        <TableCell>Check-Out</TableCell>
                        <TableCell align="right">Amount</TableCell>
                        <TableCell align="center">Status</TableCell>
                        <TableCell>Booked On</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {houseboats.map((b) => (
                        <TableRow key={b.id} sx={{ '& td': { borderColor: '#f5f3ee', fontSize: '0.85rem', color: '#444' }, '&:hover': { bgcolor: '#fdfcf9' } }}>
                          <TableCell sx={{ fontWeight: 600, color: '#333 !important' }}>{b.packageName}</TableCell>
                          <TableCell>{b.guests}</TableCell>
                          <TableCell>{b.checkIn}</TableCell>
                          <TableCell>{b.checkOut}</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600, color: `${GOLD} !important` }}>₹{(b.amount || 0).toLocaleString('en-IN')}</TableCell>
                          <TableCell align="center"><StatusChip status={b.status} /></TableCell>
                          <TableCell sx={{ color: '#aaa !important', fontSize: '0.78rem !important' }}>{fmtDate(b.createdAt)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </TabPanel>

            {/* ================ TAB 2: Donations ================ */}
            <TabPanel value={tab} index={2}>
              {loadingD ? (
                <SkeletonRows />
              ) : donations.length === 0 ? (
                <EmptyState
                  icon={<VolunteerActivismIcon sx={{ fontSize: 56 }} />}
                  title="No Donations Yet"
                  subtitle="Your donation history will appear here once you make a contribution."
                />
              ) : isMobile ? (
                <Stack spacing={1.5}>
                  {donations.map((d) => (
                    <Card
                      key={d.id}
                      variant="outlined"
                      sx={{ borderRadius: 3, borderColor: '#f0ece4', '&:hover': { borderColor: GOLD, boxShadow: `0 2px 12px rgba(212,175,55,0.12)` } }}
                    >
                      <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.8 }}>
                          <Typography sx={{ fontWeight: 700, fontSize: '0.9rem', color: '#333' }}>{d.cause}</Typography>
                          <StatusChip status={d.status} />
                        </Box>
                        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
                            <PersonIcon sx={{ fontSize: 14, color: '#bbb' }} />
                            <Typography variant="caption" sx={{ color: '#666' }}>{d.donorName}</Typography>
                          </Box>
                          {d.message && (
                            <Typography variant="caption" sx={{ color: '#999', fontStyle: 'italic' }}>
                              "{d.message}"
                            </Typography>
                          )}
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
                          <Typography sx={{ fontWeight: 700, color: GOLD, fontSize: '0.95rem' }}>
                            ₹{(d.amount || 0).toLocaleString('en-IN')}
                          </Typography>
                          <Box sx={{ textAlign: 'right' }}>
                            {d.paymentId && (
                              <Typography sx={{ color: '#bbb', fontSize: '0.65rem', fontFamily: 'monospace' }}>
                                {d.paymentId}
                              </Typography>
                            )}
                            <Typography sx={{ color: '#bbb', fontSize: '0.7rem' }}>
                              {fmtDate(d.createdAt)}
                            </Typography>
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  ))}
                </Stack>
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ '& th': { fontWeight: 700, color: '#777', fontSize: '0.78rem', borderColor: '#f0ece4', textTransform: 'uppercase', letterSpacing: '0.04em' } }}>
                        <TableCell>Cause</TableCell>
                        <TableCell>Donor Name</TableCell>
                        <TableCell>Message</TableCell>
                        <TableCell align="right">Amount</TableCell>
                        <TableCell align="center">Status</TableCell>
                        <TableCell>Payment ID</TableCell>
                        <TableCell>Date</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {donations.map((d) => (
                        <TableRow key={d.id} sx={{ '& td': { borderColor: '#f5f3ee', fontSize: '0.85rem', color: '#444' }, '&:hover': { bgcolor: '#fdfcf9' } }}>
                          <TableCell sx={{ fontWeight: 600, color: '#333 !important' }}>{d.cause}</TableCell>
                          <TableCell>{d.donorName}</TableCell>
                          <TableCell sx={{ color: '#999 !important', fontStyle: 'italic', maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {d.message || '—'}
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600, color: `${GOLD} !important` }}>₹{(d.amount || 0).toLocaleString('en-IN')}</TableCell>
                          <TableCell align="center"><StatusChip status={d.status} /></TableCell>
                          <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem !important', color: '#bbb !important' }}>
                            {d.paymentId || '—'}
                          </TableCell>
                          <TableCell sx={{ color: '#aaa !important', fontSize: '0.78rem !important' }}>{fmtDate(d.createdAt)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </TabPanel>

            {/* ================ TAB 3: Payments ================ */}
            <TabPanel value={tab} index={3}>
              {loadingP ? (
                <SkeletonRows />
              ) : payments.length === 0 ? (
                <EmptyState
                  icon={<PaymentIcon sx={{ fontSize: 56 }} />}
                  title="No Payment History"
                  subtitle="Your payment records and receipts will appear here."
                />
              ) : (
                <>
                  {/* Failed payments alert */}
                  {failedPayments > 0 && (
                    <Alert
                      severity="warning"
                      icon={<ErrorOutlineIcon />}
                      sx={{
                        mb: 2,
                        borderRadius: 2,
                        border: '1px solid rgba(255,152,0,0.3)',
                        '& .MuiAlert-message': { fontSize: '0.85rem' },
                      }}
                    >
                      You have <strong>{failedPayments}</strong> failed payment{failedPayments > 1 ? 's' : ''}.
                      Contact the temple office or retry your booking.
                    </Alert>
                  )}

                  {isMobile ? (
                    <Stack spacing={1.5}>
                      {payments.map((p) => (
                        <Card
                          key={p.id}
                          variant="outlined"
                          sx={{
                            borderRadius: 3,
                            borderColor: p.status === 'failed' ? 'rgba(211,47,47,0.3)' : '#f0ece4',
                            bgcolor: p.status === 'failed' ? 'rgba(211,47,47,0.02)' : '#fff',
                            '&:hover': { borderColor: GOLD },
                          }}
                        >
                          <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.8 }}>
                              <Box>
                                <Typography sx={{ fontWeight: 700, fontSize: '0.9rem', color: '#333' }}>{p.description}</Typography>
                                <Chip
                                  label={p.type.charAt(0).toUpperCase() + p.type.slice(1)}
                                  size="small"
                                  sx={{ mt: 0.4, height: 20, fontSize: '0.65rem', fontWeight: 600, bgcolor: '#f3f1ec', color: '#888' }}
                                />
                              </Box>
                              <StatusChip status={p.status} />
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
                              <Typography sx={{ fontWeight: 700, color: p.status === 'failed' ? '#d32f2f' : GOLD, fontSize: '0.95rem' }}>
                                ₹{(p.amount || 0).toLocaleString('en-IN')}
                              </Typography>
                              <Box sx={{ textAlign: 'right' }}>
                                {p.razorpayId && (
                                  <Typography sx={{ color: '#bbb', fontSize: '0.65rem', fontFamily: 'monospace' }}>
                                    {p.razorpayId}
                                  </Typography>
                                )}
                                <Typography sx={{ color: '#bbb', fontSize: '0.7rem' }}>
                                  {fmtDate(p.createdAt)}
                                </Typography>
                              </Box>
                            </Box>
                            {p.status === 'failed' && (
                              <Button
                                size="small"
                                variant="outlined"
                                color="error"
                                startIcon={<RefreshIcon sx={{ fontSize: 14 }} />}
                                sx={{ mt: 1, textTransform: 'none', fontWeight: 600, fontSize: '0.76rem', borderRadius: '8px' }}
                                onClick={() => navigate('/poojas/booking')}
                              >
                                Retry Booking
                              </Button>
                            )}
                          </CardContent>
                        </Card>
                      ))}
                    </Stack>
                  ) : (
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow sx={{ '& th': { fontWeight: 700, color: '#777', fontSize: '0.78rem', borderColor: '#f0ece4', textTransform: 'uppercase', letterSpacing: '0.04em' } }}>
                            <TableCell>Description</TableCell>
                            <TableCell>Type</TableCell>
                            <TableCell align="right">Amount</TableCell>
                            <TableCell align="center">Status</TableCell>
                            <TableCell>Razorpay ID</TableCell>
                            <TableCell>Date</TableCell>
                            <TableCell />
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {payments.map((p) => (
                            <TableRow
                              key={p.id}
                              sx={{
                                '& td': { borderColor: '#f5f3ee', fontSize: '0.85rem', color: '#444' },
                                bgcolor: p.status === 'failed' ? 'rgba(211,47,47,0.02)' : 'transparent',
                                '&:hover': { bgcolor: '#fdfcf9' },
                              }}
                            >
                              <TableCell sx={{ fontWeight: 600, color: '#333 !important' }}>{p.description}</TableCell>
                              <TableCell>
                                <Chip label={p.type} size="small" sx={{ height: 20, fontSize: '0.7rem', fontWeight: 600, bgcolor: '#f3f1ec', color: '#888' }} />
                              </TableCell>
                              <TableCell align="right" sx={{ fontWeight: 600, color: p.status === 'failed' ? '#d32f2f !important' : `${GOLD} !important` }}>
                                ₹{(p.amount || 0).toLocaleString('en-IN')}
                              </TableCell>
                              <TableCell align="center"><StatusChip status={p.status} /></TableCell>
                              <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem !important', color: '#bbb !important' }}>
                                {p.razorpayId || '—'}
                              </TableCell>
                              <TableCell sx={{ color: '#aaa !important', fontSize: '0.78rem !important' }}>{fmtDate(p.createdAt)}</TableCell>
                              <TableCell>
                                {p.status === 'failed' && (
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    color="error"
                                    startIcon={<RefreshIcon sx={{ fontSize: 14 }} />}
                                    sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.72rem', borderRadius: '8px' }}
                                    onClick={() => navigate('/poojas/booking')}
                                  >
                                    Retry
                                  </Button>
                                )}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </>
              )}
            </TabPanel>

            {/* ================ TAB 4: Messages ================ */}
            <TabPanel value={tab} index={4}>
              {loadingM ? (
                <SkeletonRows />
              ) : messages.length === 0 ? (
                <EmptyState
                  icon={<EmailIcon sx={{ fontSize: 56 }} />}
                  title="No Messages"
                  subtitle="Messages you send via Contact or Feedback forms will appear here with replies from the temple."
                />
              ) : (
                <Stack spacing={2}>
                  {messages.map((m) => (
                    <Card
                      key={m.id}
                      variant="outlined"
                      sx={{
                        borderRadius: 3,
                        borderColor: m.adminReply ? '#c8e6c9' : '#f0ece4',
                        '&:hover': { borderColor: GOLD, boxShadow: `0 2px 12px rgba(212,175,55,0.12)` },
                      }}
                    >
                      <CardContent sx={{ py: 2, px: 2.5, '&:last-child': { pb: 2 } }}>
                        {/* Header */}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                              <Chip
                                icon={m.formType === 'feedback' ? <FeedbackIcon sx={{ fontSize: 14 }} /> : <EmailIcon sx={{ fontSize: 14 }} />}
                                label={m.formType === 'feedback' ? 'Feedback' : 'Contact'}
                                size="small"
                                sx={{
                                  height: 22,
                                  fontSize: '0.7rem',
                                  fontWeight: 600,
                                  bgcolor: m.formType === 'feedback' ? '#fce4ec' : '#e3f2fd',
                                  color: m.formType === 'feedback' ? '#c62828' : '#1565c0',
                                }}
                              />
                              {m.category && (
                                <Chip label={m.category} size="small" sx={{ height: 22, fontSize: '0.7rem', bgcolor: '#f3f1ec', color: '#888' }} />
                              )}
                            </Box>
                            <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', color: '#333' }}>
                              {m.subject || '(no subject)'}
                            </Typography>
                          </Box>
                          <Box sx={{ textAlign: 'right', flexShrink: 0, ml: 1 }}>
                            <Typography sx={{ color: '#bbb', fontSize: '0.72rem' }}>{fmtDate(m.createdAt)}</Typography>
                            {m.adminReply ? (
                              <Chip label="Replied" size="small" icon={<ReplyIcon sx={{ fontSize: 12 }} />}
                                sx={{ height: 20, fontSize: '0.65rem', fontWeight: 600, bgcolor: '#e8f5e9', color: '#2e7d32', mt: 0.5 }}
                              />
                            ) : (
                              <Chip label="Awaiting Reply" size="small"
                                sx={{ height: 20, fontSize: '0.65rem', fontWeight: 600, bgcolor: '#fff3e0', color: '#e65100', mt: 0.5 }}
                              />
                            )}
                          </Box>
                        </Box>

                        {/* Message body */}
                        <Paper sx={{ p: 1.5, bgcolor: '#f9f9f9', borderRadius: 2, mb: m.adminReply ? 1.5 : 0 }}>
                          <Typography sx={{ fontSize: '0.85rem', color: '#555', whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>
                            {m.message}
                          </Typography>
                        </Paper>

                        {/* Admin Reply */}
                        {m.adminReply && (
                          <Box sx={{ pl: 2, borderLeft: '3px solid #4caf50' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                              <ReplyIcon sx={{ fontSize: 14, color: '#2e7d32' }} />
                              <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#2e7d32' }}>
                                Temple Reply
                              </Typography>
                              <Typography sx={{ fontSize: '0.7rem', color: '#999', ml: 'auto' }}>
                                {fmtDate(m.repliedAt)}
                              </Typography>
                            </Box>
                            <Paper sx={{ p: 1.5, bgcolor: '#e8f5e9', borderRadius: 2, border: '1px solid #c8e6c9' }}>
                              <Typography sx={{ fontSize: '0.85rem', color: '#333', whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>
                                {m.adminReply}
                              </Typography>
                            </Paper>
                          </Box>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </Stack>
              )}
            </TabPanel>
          </Box>
        </Paper>
      </Container>
    </>
  );
}
